import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Sparkles, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface CommentSuggestionsProps {
  postContent: string;
  postCategory?: string;
  onSelectSuggestion: (suggestion: string) => void;
  isVisible: boolean;
}

export const CommentSuggestions = ({ 
  postContent, 
  postCategory, 
  onSelectSuggestion,
  isVisible 
}: CommentSuggestionsProps) => {
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);
  const { toast } = useToast();

  const generateSuggestions = async () => {
    if (hasGenerated) return; // Only generate once per session
    
    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('suggest-comments', {
        body: { 
          postContent: postContent.substring(0, 500), // Limit content length
          postCategory 
        }
      });

      if (error) throw error;

      setSuggestions(data.suggestions || []);
      setHasGenerated(true);
    } catch (error) {
      console.error('Error generating suggestions:', error);
      toast({
        title: "Could not generate suggestions",
        description: "Try typing your own authentic comment",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!isVisible) return null;

  return (
    <Card className="p-3 mt-2 bg-gradient-to-r from-primary/5 to-accent/5 border-primary/20">
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="w-4 h-4 text-primary" />
        <span className="text-sm font-medium">Comment suggestions</span>
      </div>
      
      {!hasGenerated && !isLoading && (
        <Button
          variant="outline"
          size="sm"
          onClick={generateSuggestions}
          className="w-full text-xs"
        >
          <Sparkles className="w-3 h-3 mr-1" />
          Get suggestions
        </Button>
      )}

      {isLoading && (
        <div className="flex items-center justify-center py-2">
          <Loader2 className="w-4 h-4 animate-spin mr-2" />
          <span className="text-sm text-muted-foreground">Thinking...</span>
        </div>
      )}

      {suggestions.length > 0 && (
        <div className="space-y-1">
          {suggestions.map((suggestion, index) => (
            <Button
              key={index}
              variant="ghost"
              size="sm"
              className="w-full text-left justify-start h-auto py-2 px-2 text-xs whitespace-normal"
              onClick={() => onSelectSuggestion(suggestion)}
            >
              "{suggestion}"
            </Button>
          ))}
          <p className="text-xs text-muted-foreground mt-2">
            Or write your own authentic response
          </p>
        </div>
      )}
    </Card>
  );
};